import React, { useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, Outlet } from 'react-router-dom'
import { Stack, useTheme, useMediaQuery, Box } from '@mui/material'
import { Button } from '@center/ui-kit'

import EmptyScreen from 'packages/ui/EmptyScreen'
import { useUser } from 'store'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import HeaderExportButton from 'components/global/Buttons/HeaderExportButton'
import { useSearchParams, useNavigate } from 'router'
import MainContent, { TableContent } from 'components-shared'
import useSubscribeRefresh  from 'use-subscribe-actions'
import TextFilter from '../../components/TextFilter'
import Filters, { FILTER_LAYOUT_TYPES } from './Filters'
import CardInMass from './Card/CardInMass'
import useFilters, { useResetFilters, useCouponsSelected } from './store'
import useSearch from './use-search'
import Table from './Table'
import { trackEvent } from 'analytics'


const SearchAndStatusRow = ({ isMobile = false, verbatim, coupons, isStandard, onVerbatimChange }) => {
  const {t} = useTranslation()
  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: '8px',
        width: '100%'
      }}
    >
      <Box sx={{ flex: isMobile ? 1 : 'initial' }}>
        <TextFilter
          placeholder={t('coupons.filters.search')}
          value={verbatim}
          onChange={onVerbatimChange}
          sx={isMobile ? { width: '100%' } : {}}
        />
      </Box>
      <Box sx={{ flex: isMobile ? 1 : 'initial' }}>
        <Filters
          coupons={coupons}
          isStandard={isStandard}
          layout={isMobile ? FILTER_LAYOUT_TYPES.MOBILE_COMPACT : FILTER_LAYOUT_TYPES.DESKTOP}
        />
      </Box>
    </Stack>
  )
}

const CouponsView = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const [couponsSelected] = useCouponsSelected()
  const theme = useTheme()
  const [user] = useUser()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'xl'))
  const [filters, setFilters] = useFilters()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const searchStarted = useRef(false)
  useSubscribeRefresh(() => navigate('/coupons'))

  const resetFilters = useResetFilters()
  
  const isStandard = useMemo(() => {
    return location.pathname.includes('standard')
  }, [location.pathname])

  const apiSearch = useSearch({ standard: isStandard })
  const hasData = useMemo(() => {
    if (apiSearch.loading) {
      return true
    }
    return Boolean(apiSearch.data && apiSearch.data.coupons.length)
  }, [apiSearch.data])

  useEffect(() => {
    return () => {
      resetFilters()
    }
  }, [isStandard])

  useEffect(() => {
    if (searchParams.get('availability')) {
      setFilters(prev => ({
        ...prev,
        availability: searchParams.get('availability')
      }))
    }
    return () => resetFilters()
  }, [])

  const handleVerbatimChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, verbatim: value }))
    const hasSearch = value.trim().length > 2

    if (hasSearch && !searchStarted.current) {
      trackEvent('search_code', { code_type: isStandard ? 'standard' : 'h3' })
    }

    searchStarted.current = hasSearch
  }, [isStandard, setFilters])

  const couponsData = useMemo(() => apiSearch.data, [apiSearch.data])
  const exportAction = useMemo(() => (
    <HeaderExportButton
      analyticsName="codes"
      onClick={() => navigate({ pathname: `${location.pathname}/export` })}
      disabled={Boolean(user.demo_mode)}
    >
      {t('export.button')}
    </HeaderExportButton>
  ), [location.pathname, navigate, t, user.demo_mode])

  useRegisterBreadcrumbAction(exportAction)

  const actionButtonsElement = useMemo(() => (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: '16px',
        flexShrink: 0
      }}
    >
      {isStandard && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<i className={'fa-regular fa-plus'} />}
          size="12px"
          disabled={Boolean(user.demo_mode)}
          onClick={() => {
            trackEvent('code_create', { code_type: 'standard' })
            navigate({ pathname: `${location.pathname}/new${location.search}` })
          }}
          sx={{
            height: '32px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            padding: '0 16px 2px 12px',
            fontFamily: 'Poppins'
          }}
        >
          {t('coupons.create.button')}
        </Button>
      )}
    </Stack>
  ), [t, navigate, location.pathname, location.search, user.demo_mode, isStandard])

  const mobileLayout = useMemo(() => (
    <Stack gap={1}>
      <SearchAndStatusRow 
        isMobile={true} 
        verbatim={filters.verbatim}
        coupons={couponsData}
        isStandard={isStandard}
        onVerbatimChange={handleVerbatimChange}
      />
      
      <Filters
        coupons={couponsData}
        isStandard={isStandard}
        layout={FILTER_LAYOUT_TYPES.MOBILE_EXTENDED}
        dateTypes={['creation', 'usage']}
      />
      
      <Stack direction="row" justifyContent="flex-end">
        {actionButtonsElement}
      </Stack>
    </Stack>
  ), [filters, t, couponsData, isStandard, handleVerbatimChange, actionButtonsElement])

  const tabletLayout = useMemo(() => (
    <Stack gap={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextFilter
            placeholder={t('coupons.filters.search')}
            value={filters.verbatim}
            onChange={handleVerbatimChange}
            sx={{ width: '100%', mb: 1 }}
          />
        </Box>
        {actionButtonsElement}
      </Stack>
      
      <Filters
        coupons={couponsData}
        isStandard={isStandard}
        layout={FILTER_LAYOUT_TYPES.TABLET}
        dateTypes={['creation', 'usage']}
      />
    </Stack>
  ), [filters.verbatim, t, handleVerbatimChange, couponsData, isStandard, actionButtonsElement])

  const desktopLayout = useMemo(() => (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack
        sx={{
          alignItems: 'center',
          flexDirection: 'row',
          gap: '8px',
          flex: 'initial',
          minWidth: 0
        }}
      >
        <SearchAndStatusRow 
          isMobile={false} 
          verbatim={filters.verbatim}
          t={t}
          coupons={couponsData}
          isStandard={isStandard}
          onVerbatimChange={handleVerbatimChange}
        />
      </Stack>
      {actionButtonsElement}
    </Stack>
  ), [filters, t, couponsData, isStandard, handleVerbatimChange, actionButtonsElement])

  const layout = useMemo(() => {
    if (isMobile) return mobileLayout
    if (isTablet) return tabletLayout
    return desktopLayout
  }, [isMobile, isTablet, mobileLayout, tabletLayout, desktopLayout])

  return (
    <Stack gap={1} flex={1}>
      {layout}

      <MainContent>
        <TableContent>
          {
            hasData
              ? <Table apiSearch={apiSearch} isStandard={isStandard} />
              : <EmptyScreen icon="wifi">{t('coupons.no_data_filter')}</EmptyScreen>
          }

        </TableContent>
        {
          couponsSelected.length
            ? <CardInMass />
            : <Outlet context={{ apiSearch, isStandard }} />
        }
      </MainContent>
    </Stack>
  )
}

export default CouponsView
